/**
 * UNIFIED LOGIN — `/login`.
 *
 * ONE account per email, ONE screen, two ways in. Role is never chosen here;
 * it is a property of the account resolved from `user_roles` after auth.
 *
 * Anti-enumeration discipline (this is the whole point of the shape below):
 *   - Every step renders inside a card carrying data-testid="screen-login".
 *   - Step 2 offers BOTH methods (password + "email me a code") for EVERY
 *     email. We deliberately do NOT branch on "does this account have a
 *     password", because the resolver does not expose that — and exposing it
 *     would turn step 2 into a real-account oracle. A consumer created by OTP
 *     simply uses the code option; a graduated/staff account may use either.
 *   - The OTP request is fired with shouldCreateUser:false and its result is
 *     swallowed: unknown and real emails both advance to the same code step
 *     with the same copy. Verification failures collapse into one uniform
 *     bucket.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth-context";
import { resolveAuthState } from "@/lib/auth/callResolver";
import { sanitizeNext, resolveLanding } from "@/lib/auth/next";
import { supabase } from "@/integrations/supabase/client";
import { OTP_LENGTH, OTP_LENGTH_LABEL, isCompleteOtp, normalizeOtpInput } from "@/lib/otp";
import PasswordInput from "@/components/PasswordInput";
import MfaChallenge from "@/components/MfaChallenge";
import { ArrowLeft, Loader2 } from "lucide-react";

type Step = "email" | "method" | "code";

const UNIFORM_SIGNIN_ERROR = "That email and password combination did not work.";
const UNIFORM_CODE_ERROR = "That code didn't work. Check it and try again, or send a new one.";

const Login = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { user, loading: authLoading, mfaRequired, signIn, signOut, completeMfaChallenge, refreshRoles } = useAuth();

  const next = useMemo(() => sanitizeNext(params.get("next")), [params]);
  const staff = params.get("staff") === "1";

  const [step, setStep] = useState<Step>("email");
  const [email, setEmail] = useState(params.get("email") ?? "");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [landing, setLanding] = useState(false);

  // Post-auth landing. Runs once a session exists and MFA (if enrolled) is
  // satisfied. Roles are re-read from the server before we decide where to go,
  // so we never land a user on a surface their roles don't cover.
  useEffect(() => {
    if (authLoading || !user || mfaRequired || landing) return;
    setLanding(true);
    (async () => {
      const roles = await refreshRoles();
      navigate(
        resolveLanding({ next, isSuperAdmin: roles.isSuperAdmin, isAdmin: roles.isAdmin }),
        { replace: true }
      );
    })();
  }, [authLoading, user, mfaRequired, landing, next, refreshRoles, navigate]);

  if (!authLoading && user && mfaRequired) {
    return <MfaChallenge onVerified={completeMfaChallenge} onCancel={signOut} />;
  }

  // Anti-enumeration timing floor. The server-side resolver already has its own
  // 150ms floor (P2.1), but the round-trips it makes differ measurably between a
  // known and an unknown address (edge-fn work, mailer call). Padding every
  // branch out to the same wall-clock minimum removes that side channel.
  const TIMING_FLOOR_MS = 1200;
  const withTimingFloor = async <T,>(work: Promise<T>): Promise<T> => {
    const started = Date.now();
    try {
      return await work;
    } finally {
      const remaining = TIMING_FLOOR_MS - (Date.now() - started);
      if (remaining > 0) await new Promise((r) => setTimeout(r, remaining));
    }
  };

  const sendCode = async (target: string) => {
    // Result deliberately ignored — see the anti-enumeration note above.
    try {
      await supabase.auth.signInWithOtp({
        email: target,
        options: { shouldCreateUser: false },
      });
    } catch {
      /* uniform */
    }
  };

  const handleEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!email.trim()) {
      setError("Enter your email address.");
      return;
    }
    setBusy(true);
    // Resolver-first: server-truth routing for the states that have their own
    // dedicated, already-hardened screens (P2.7).
    const resolved = await withTimingFloor(resolveAuthState(email.trim()));
    setBusy(false);

    if (resolved.screen_key === "invite_expired" || resolved.screen_key === "account_setup") {
      const q = new URLSearchParams({ email: email.trim() });
      if (resolved.screen_key === "invite_expired") q.set("expired", "1");
      navigate(`/admin/accept-invite?${q.toString()}`, { replace: true });
      return;
    }
    if (resolved.screen_key === "reset_pending" || resolved.screen_key === "reset_expired") {
      const q = new URLSearchParams({ email: email.trim() });
      if (resolved.screen_key === "reset_expired") q.set("expired", "1");
      navigate(`/admin/reset-password?${q.toString()}`, { replace: true });
      return;
    }

    setStep("method");
  };

  const handlePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!password) {
      setError(UNIFORM_SIGNIN_ERROR);
      return;
    }
    setBusy(true);
    const { error: signInError } = await signIn(email.trim(), password);
    setBusy(false);
    if (signInError) setError(UNIFORM_SIGNIN_ERROR);
    // Success: the landing effect takes over.
  };

  const chooseCode = async () => {
    setError(null);
    setBusy(true);
    await withTimingFloor(sendCode(email.trim()));
    setBusy(false);
    setNotice(`If that address has an account, we've emailed an ${OTP_LENGTH_LABEL} code. It expires in an hour.`);
    setStep("code");
  };

  const handleCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!isCompleteOtp(code)) {
      setError(UNIFORM_CODE_ERROR);
      return;
    }
    setBusy(true);
    const { error: verifyError } = await supabase.auth.verifyOtp({
      email: email.trim(),
      token: code,
      type: "email",
    });
    setBusy(false);
    if (verifyError) setError(UNIFORM_CODE_ERROR);
    // Success: the landing effect takes over.
  };

  const forgotPassword = async () => {
    setError(null);
    setBusy(true);
    await supabase.functions.invoke("request-password-reset", { body: { email: email.trim() } });
    setBusy(false);
    navigate(`/admin/reset-password?email=${encodeURIComponent(email.trim())}`);
  };

  const inputClass =
    "h-11 w-full rounded-[12px] border border-ink/15 bg-background px-3.5 text-[15px] text-ink outline-none focus:border-grape focus:ring-[3px] focus:ring-grape/20";

  return (
    <div className="flex min-h-screen items-center justify-center bg-paper p-6 font-brand-body">
      <div className="w-full max-w-[400px]">
        <Link
          to="/"
          className="mb-8 inline-flex items-center gap-1.5 text-[13px] font-medium text-slate-muted transition-colors hover:text-grape"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Back to Cardmoji
        </Link>

        <div
          data-testid="screen-login"
          className="w-full rounded-[22px] border border-ink/10 bg-white p-6 shadow-[0_24px_60px_-32px_rgba(28,32,48,0.35)]"
        >
          <h1 className="font-brand text-[22px] font-semibold tracking-[-0.01em] text-ink">
            {staff ? "Cardmoji staff sign-in" : "Sign in to Cardmoji"}
          </h1>
          <p className="mb-5 mt-1.5 text-[14px] leading-[1.5] text-slate-muted">
            One account, one sign-in. What you can reach is set by your account — you'll land in
            the right place and can switch between your contexts afterwards.
          </p>

          {step === "email" && (
            <form onSubmit={handleEmail} className="space-y-4" data-testid="login-step-email">
              <div className="space-y-1.5">
                <label htmlFor="login-email" className="block text-[13px] font-semibold text-ink">
                  Email
                </label>
                <input
                  id="login-email"
                  data-testid="login-email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className={inputClass}
                  required
                />
              </div>
              <button
                type="submit"
                data-testid="login-continue"
                disabled={busy}
                className="flex h-11 w-full items-center justify-center gap-2 rounded-[12px] bg-ink text-[15px] font-semibold text-ink-foreground transition-colors hover:bg-grape-deep disabled:opacity-70"
              >
                {busy && <Loader2 className="h-4 w-4 animate-spin" />}
                Continue
              </button>
            </form>
          )}

          {step === "method" && (
            <div className="space-y-4" data-testid="login-step-method">
              <p className="text-[13px] text-slate-muted">
                Signing in as <span className="font-semibold text-ink">{email}</span>{" "}
                <button
                  type="button"
                  onClick={() => {
                    setStep("email");
                    setError(null);
                    setPassword("");
                  }}
                  className="font-medium text-grape underline"
                >
                  change
                </button>
              </p>
              <form onSubmit={handlePassword} className="space-y-4">
                <PasswordInput
                  id="login-password"
                  label={<span className="text-[13px] font-semibold text-ink">Password</span>}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  autoComplete="current-password"
                  data-testid="login-password"
                  className="h-11 rounded-[12px] border-ink/15 bg-background text-[15px]"
                />
                <button
                  type="submit"
                  data-testid="login-submit-password"
                  disabled={busy}
                  className="flex h-11 w-full items-center justify-center gap-2 rounded-[12px] bg-ink text-[15px] font-semibold text-ink-foreground transition-colors hover:bg-grape-deep disabled:opacity-70"
                >
                  {busy && <Loader2 className="h-4 w-4 animate-spin" />}
                  Sign in
                </button>
              </form>
              <div className="flex items-center gap-3">
                <span className="h-px flex-1 bg-ink/10" />
                <span className="text-[12px] font-semibold uppercase tracking-[0.08em] text-slate-muted">or</span>
                <span className="h-px flex-1 bg-ink/10" />
              </div>
              <button
                type="button"
                data-testid="login-use-code"
                onClick={chooseCode}
                disabled={busy}
                className="h-11 w-full rounded-[12px] border border-ink/20 bg-white text-[15px] font-semibold text-ink transition-colors hover:border-ink disabled:opacity-70"
              >
                Email me a {OTP_LENGTH_LABEL} code instead
              </button>
              <button
                type="button"
                onClick={forgotPassword}
                disabled={busy}
                className="w-full text-center text-[12.5px] font-medium text-slate-muted transition-colors hover:text-grape"
              >
                Forgot your password?
              </button>
            </div>
          )}

          {step === "code" && (
            <form onSubmit={handleCode} className="space-y-4" data-testid="login-step-code">
              {notice && (
                <p className="text-[13px] leading-[1.5] text-slate-muted" data-testid="login-code-notice">
                  {notice}
                </p>
              )}
              <div className="space-y-1.5">
                <label htmlFor="login-code" className="block text-[13px] font-semibold text-ink">
                  Your code
                </label>
                <input
                  id="login-code"
                  data-testid="login-code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={OTP_LENGTH}
                  value={code}
                  onChange={(e) => setCode(normalizeOtpInput(e.target.value))}
                  placeholder={`${OTP_LENGTH_LABEL} code`}
                  className={`${inputClass} tracking-[0.32em]`}
                />
              </div>
              <button
                type="submit"
                data-testid="login-submit-code"
                disabled={busy}
                className="flex h-11 w-full items-center justify-center gap-2 rounded-[12px] bg-ink text-[15px] font-semibold text-ink-foreground transition-colors hover:bg-grape-deep disabled:opacity-70"
              >
                {busy && <Loader2 className="h-4 w-4 animate-spin" />}
                Verify and sign in
              </button>
              <div className="flex items-center justify-between">
                <button
                  type="button"
                  onClick={() => chooseCode()}
                  disabled={busy}
                  className="text-[12.5px] font-medium text-slate-muted transition-colors hover:text-grape"
                >
                  Send a new code
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setStep("method");
                    setCode("");
                    setError(null);
                    setNotice(null);
                  }}
                  className="text-[12.5px] font-medium text-slate-muted transition-colors hover:text-grape"
                >
                  Use a password
                </button>
              </div>
            </form>
          )}

          {error && (
            <p className="mt-4 text-[13px] font-medium text-[#C41112]" data-testid="login-error">
              {error}
            </p>
          )}
        </div>

        <p className="mt-5 text-center text-[12.5px] leading-relaxed text-slate-muted">
          Consumers, creators, bank staff and platform operators all sign in here. Your access is
          set by your account, never chosen at sign-in.
        </p>
      </div>
    </div>
  );
};

export default Login;
